/* =========================================================
   OVERTOP — Variant Media Filter v4.1.0
   ---------------------------------------------------------
   Slideshow-slide aware media filtering for Horizon.

   Uses shared OT_PRODUCT_DATA_CACHE, prewarms the target
   variant image on intent, and reapplies once after Horizon's
   server-rendered variant morph completes.
   ========================================================= */

(function () {
  'use strict';

  var VERSION = '4.5.0';
  var productData = null;
  var mediaIdToPosition = {};   // media.id -> position
  var mediaIdToFile = {};       // media.id -> src filename (for thumbnail matching)
  var variantOwnership = {};    // variant.id -> { start, end }
  var loadPromise = null;
  var currentVariantId = null;  // last variant we filtered to
  var lastApplyAt = 0;          // suppress observer feedback loop

  window.__OT_VARIANT_MEDIA_FILTER_VERSION = VERSION;

  function getHandle() {
    var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  function rootUrl() {
    try {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        return String(window.Shopify.routes.root || '/').replace(/\/$/, '');
      }
    } catch (err) {}
    return '';
  }

  function getProductDataCache() {
    if (window.OT_PRODUCT_DATA_CACHE && window.OT_PRODUCT_DATA_CACHE.fetchByHandle) {
      return window.OT_PRODUCT_DATA_CACHE;
    }

    var cache = {
      products: {},
      promises: {},
      fetchByHandle: function (handle) {
        handle = String(handle || '').trim();
        if (!handle) return Promise.resolve(null);
        if (cache.products[handle]) return Promise.resolve(cache.products[handle]);
        if (cache.promises[handle]) return cache.promises[handle];

        cache.promises[handle] = fetch(rootUrl() + '/products/' + encodeURIComponent(handle) + '.js', {
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' }
        }).then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        }).then(function (product) {
          cache.products[handle] = product;
          if (product && product.handle) cache.products[product.handle] = product;
          return product;
        }).catch(function (err) {
          delete cache.promises[handle];
          throw err;
        });

        return cache.promises[handle];
      },
      getByHandle: function (handle) {
        return cache.products[String(handle || '').trim()] || null;
      }
    };

    window.OT_PRODUCT_DATA_CACHE = cache;
    return cache;
  }

  function getMediaPosition(mediaId) {
    return mediaIdToPosition[String(mediaId)];
  }

  function loadProduct() {
    if (loadPromise) return loadPromise;
    var handle = getHandle();
    if (!handle) return Promise.resolve(null);

    loadPromise = getProductDataCache().fetchByHandle(handle).then(function (product) {
      productData = product;
      buildOwnership(product);
      return product;
    }).catch(function () {
      return null;
    });

    return loadPromise;
  }

  function buildOwnership(product) {
    mediaIdToPosition = {};
    mediaIdToFile = {};
    variantOwnership = {};

    if (!product || !product.media) return;

    product.media.forEach(function (media) {
      mediaIdToPosition[String(media.id)] = media.position;
      var src = media.src || (media.preview_image && media.preview_image.src) || '';
      var file = String(src).split('?')[0].split('/').pop();
      if (file) mediaIdToFile[String(media.id)] = file;
    });

    var variantsWithFeatured = (product.variants || [])
      .map(function (variant) {
        var fmId = String(
          (variant.featured_media && variant.featured_media.id) ||
          (variant.featured_image && variant.featured_image.id) ||
          ''
        );
        return { id: String(variant.id || ''), featPos: mediaIdToPosition[fmId] };
      })
      .filter(function (variant) { return variant.id && typeof variant.featPos === 'number'; })
      .sort(function (a, b) { return a.featPos - b.featPos; });

    if (variantsWithFeatured.length < 2) return;

    for (var i = 0; i < variantsWithFeatured.length; i++) {
      var startPos = variantsWithFeatured[i].featPos;
      var endPos = (i + 1 < variantsWithFeatured.length)
        ? variantsWithFeatured[i + 1].featPos - 1
        : Infinity;
      variantOwnership[String(variantsWithFeatured[i].id)] = { start: startPos, end: endPos };
    }
  }

  function styleEl() {
    var s = document.getElementById('ot-vmf-style');
    if (!s) {
      s = document.createElement('style');
      s.id = 'ot-vmf-style';
      (document.head || document.documentElement).appendChild(s);
    }
    return s;
  }

  /* Build a persistent CSS rule that hides every media OUTSIDE the selected
     variant's position range. Lives in <head>, so Horizon's section morph
     (which rebuilds the gallery + thumbnails) never wipes it, and it applies
     to freshly-rendered nodes automatically — no timing, no re-apply race.

     Main slides carry data-media-id (hide the slide via :has, plus the inner
     element as a fallback). Thumbnails have NO data-media-id — they are
     index-based (on:click="/select/N", N = position - 1, in media order),
     which is stable only when Horizon renders ALL media: requires
     hide_variants:false in templates/product.json. */
  function buildCss(range) {
    if (!range) return '';
    var rules = [];
    Object.keys(mediaIdToPosition).forEach(function (mid) {
      var pos = mediaIdToPosition[mid];
      if (typeof pos !== 'number') return;
      if (pos < range.start || pos > range.end) {
        // Hide only the THUMBNAILS for off-colour media (match by <img>
        // filename — thumbnails have no data-media-id). Main slides are left
        // visible so native thumbnail navigation + swipe keep working.
        var file = mediaIdToFile[mid];
        if (file) {
          rules.push('.slideshow-controls__thumbnail:has(img[src*="' + file + '"])');
          rules.push('.dialog-thumbnails-list__thumbnail:has(img[src*="' + file + '"])');
        }
      }
    });
    return rules.length ? rules.join(',') + '{display:none !important;}' : '';
  }

  function inRange(pos, range) {
    return !!range && typeof pos === 'number' && pos >= range.start && pos <= range.end;
  }

  function setHidden(el, hide) {
    if (!el) return;
    if (hide) {
      el.setAttribute('hidden', '');
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('data-ot-hidden', '1');
    } else if (el.getAttribute('data-ot-hidden') === '1') {
      el.removeAttribute('hidden');
      el.removeAttribute('aria-hidden');
      el.removeAttribute('data-ot-hidden');
    }
  }

  function fileFromSrc(src) {
    return String(src || '').split('?')[0].split('/').pop();
  }

  /* Direct DOM hiding of THUMBNAILS only. We deliberately do NOT hide the main
     slides: hiding them shrinks Horizon's visible-slide set, which breaks the
     thumbnails' native /select/N navigation and lets our re-apply reset the
     slideshow position mid-click. With all slides present, native thumbnail
     clicks + swipe work untouched; we only trim the thumbnail strip to the
     selected colour. Thumbnails carry no data-media-id, so match by <img>
     filename. Re-run by the gallery MutationObserver after each morph. */
  function domFilter(range) {
    if (!range) return;

    var disallowedFiles = {};
    Object.keys(mediaIdToPosition).forEach(function (mid) {
      var p = mediaIdToPosition[mid];
      if (!inRange(p, range) && mediaIdToFile[mid]) disallowedFiles[mediaIdToFile[mid]] = true;
    });
    document.querySelectorAll(
      '.slideshow-controls__thumbnail, .dialog-thumbnails-list__thumbnail'
    ).forEach(function (btn) {
      var img = btn.querySelector('img');
      var file = fileFromSrc(img && (img.getAttribute('src') || img.src));
      setHidden(btn, !!disallowedFiles[file]);
    });
  }

  function applyFilter(variantId) {
    if (!variantId) return;
    currentVariantId = String(variantId);
    var range = variantOwnership[String(variantId)];
    styleEl().textContent = buildCss(range);
    domFilter(range);
    lastApplyAt = Date.now();
  }

  /* Safety net: if a variant changes without one of the events below firing,
     re-derive the live checked option whenever the gallery DOM mutates and
     refresh the style. Updating a <head> <style> never mutates the gallery,
     so this cannot loop. */
  var reapplyScheduled = false;
  function reapplyCurrent() {
    observeGalleries();
    var id = getVariantIdFromCheckedOption() ||
             new URL(location.href).searchParams.get('variant') ||
             currentVariantId ||
             getInitialVariantId();
    if (id) applyFilter(id);
  }
  function scheduleReapply() {
    if (reapplyScheduled) return;
    reapplyScheduled = true;
    requestAnimationFrame(function () { reapplyScheduled = false; reapplyCurrent(); });
  }
  function observeGalleries() {
    var gals = document.querySelectorAll('media-gallery');
    Array.prototype.forEach.call(gals, function (g) {
      if (g.__otFilterObserved) return;
      g.__otFilterObserved = true;
      new MutationObserver(scheduleReapply).observe(g, { childList: true, subtree: true });
    });
  }

  function preloadVariant(variantId, priority) {
    try {
      if (window.OT_IMAGE_PRELOAD_VARIANT) {
        window.OT_IMAGE_PRELOAD_VARIANT(variantId, priority || 'high');
      }
    } catch (err) {}
  }

  function getInitialVariantId() {
    if (!productData) return null;
    var url = new URL(window.location.href);
    var qVariant = url.searchParams.get('variant');
    if (qVariant) {
      var match = (productData.variants || []).find(function (variant) { return String(variant.id) === String(qVariant); });
      if (match) return String(match.id);
    }
    var avail = (productData.variants || []).find(function (variant) { return variant.available; });
    if (avail) return String(avail.id);
    return productData.variants && productData.variants[0] && String(productData.variants[0].id);
  }

  function getVariantIdFromCheckedOption() {
    var checked = document.querySelector('variant-picker input:checked[data-variant-id], variant-picker option:checked[data-variant-id]');
    if (checked && checked.dataset && checked.dataset.variantId) return checked.dataset.variantId;
    return null;
  }

  function getVariantIdFromTarget(target) {
    if (!target || !target.closest) return null;

    var direct = target.closest('[data-variant-id]');
    if (direct && direct.dataset && direct.dataset.variantId) return direct.dataset.variantId;

    var label = target.closest('label, .variant-option__button-label');
    if (label) {
      var nested = label.querySelector('[data-variant-id]');
      if (nested && nested.dataset && nested.dataset.variantId) return nested.dataset.variantId;
    }

    return null;
  }

  function variantFromEvent(event) {
    var variant = event && event.detail && (event.detail.resource || event.detail.variant);
    return variant && variant.id ? String(variant.id) : null;
  }

  function reapplyWithRetries(variantId) {
    if (!variantId) return;
    preloadVariant(variantId, 'high');
    applyFilter(variantId);
    setTimeout(function () { applyFilter(variantId); }, 60);
    setTimeout(function () { applyFilter(variantId); }, 220);
    setTimeout(function () { applyFilter(variantId); }, 520);
  }

  function applyWhenReady(variantId) {
    if (!variantId) return;
    preloadVariant(variantId, 'high');
    if (productData) {
      applyFilter(variantId);
      return;
    }
    loadProduct().then(function () { applyFilter(variantId); });
  }

  function boot() {
    if (!getHandle()) return;

    loadProduct().then(function (product) {
      if (!product) return;
      observeGalleries();
      var initialId = getInitialVariantId();
      if (initialId) reapplyWithRetries(initialId);
    });

    document.addEventListener('pointerdown', function (event) {
      var variantId = getVariantIdFromTarget(event.target);
      if (variantId) applyWhenReady(variantId);
    }, { passive: true, capture: true });

    document.addEventListener('touchstart', function (event) {
      var variantId = getVariantIdFromTarget(event.target);
      if (variantId) applyWhenReady(variantId);
    }, { passive: true, capture: true });

    document.addEventListener('variant:selected', function () {
      Promise.resolve().then(function () {
        var variantId = getVariantIdFromCheckedOption() || new URL(location.href).searchParams.get('variant');
        if (variantId) applyWhenReady(variantId);
      });
    });

    document.addEventListener('variant:update', function (event) {
      var variantId = variantFromEvent(event) || new URL(location.href).searchParams.get('variant');
      if (!variantId) return;

      if (productData) {
        reapplyWithRetries(variantId);
      } else {
        loadProduct().then(function () { reapplyWithRetries(variantId); });
      }
    });

    document.addEventListener('shopify:section:load', function () {
      setTimeout(function () {
        observeGalleries();
        var variantId = getVariantIdFromCheckedOption() ||
                        new URL(location.href).searchParams.get('variant') ||
                        getInitialVariantId();
        if (variantId) applyWhenReady(variantId);
      }, 120);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
